import InventoryItemDealerPortalTypeEnum from 'enums/InventoryItemDealerPortalTypeEnum';
import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import Cart from 'routes/Pages/Cart/Cart';
import Checkout from 'routes/Pages/Checkout/Checkout';
import Profile from 'routes/Pages/Profile/Profile';
import InventoryItemCategory from 'routes/Pages/InventoryItemCategories/InventoryItemCategory';
import { usePrefetchCart } from 'services/Data/CartService';
import Wall from 'routes/Pages/Wall/Wall';
import Tooling from 'routes/Pages/Tooling/Tooling';
import HomePage from 'routes/Pages/HomePage/HomePage';
import ExteriorSignageQuoteLandingInfo from 'routes/Pages/ExteriorSignageQuote/ExteriorSignageQuoteLandingInfo';
import ExteriorSignageQuote from 'routes/Pages/ExteriorSignageQuote/ExteriorSignageQuote';
import ExteriorSignageQuoteCheckout from 'routes/Pages/ExteriorSignageQuote/ExteriorSignageQuoteCheckout';
import MerchandisingProgramQuoteLandingInfo from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuoteLandingInfo';
import MerchandisingProgramQuote from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuote';
import MerchandisingProgramQuoteCheckout from 'routes/Pages/MerchandisingProgramQuote/MerchandisingProgramQuoteCheckout';
import UsefulLinks from 'routes/Pages/UsefulLink/UsefulLinks';
import Supports from 'routes/Pages/Support/Supports';
import Certification from 'routes/Pages/Certification/Certification';
import DealerPortalMenuProducts from 'routes/Pages/DealerPortalMenu/DealerPortalMenuProducts';
import DealerPortalMenuProductDetail from 'routes/Pages/DealerPortalMenu/DealerPortalMenuProductDetail';
import { usePrefetchApprovalNotification } from 'services/Data/NotificationService';
import { homePage, IsDSM, IsImpersonationMode, IsMerchandisingProgram, IsProduction, PODUrl } from 'config/config';
import { useSelector } from 'react-redux';
import { isPrintOnDemandSection, shouldUseLegacyPodUrl } from 'services/Utilities/podRoutingUtils';

const LegacyPodRedirect = () => {
  if (typeof window !== 'undefined') {
    window.location.replace(PODUrl);
  }

  return null;
};

const ApplicationLayout = () => {
  const accountReducer = useSelector(state => state.AccountReducer);

  const dealerId = accountReducer?.profile?.dealer_Id;
  usePrefetchCart(accountReducer.isLoggedIn, IsImpersonationMode ? dealerId : null);
  usePrefetchApprovalNotification(accountReducer.isLoggedIn);

  const isDisplaysToolingAndFurnitureSection = sectionName =>
    String(sectionName || '').toLowerCase() === 'displaystoolingandfurniture';

  const getInventoryTypeEditComponent = categoryName => {
    switch (categoryName) {
      case 'Walls':
        return Wall;
      case 'Tooling':
        return Tooling;
      case 'OtherTooling':
      case 'MountedDisplay':
        return Tooling;
      case 'WallPanelComponent':
      case 'MerchandisingTooling':
      case 'SignHolder':
      case 'FloorDisplay':
      case 'Furniture':
      case 'PartsServices':
      case 'VehiclePurpose':
      case 'Ceiling':
        return InventoryItemCategory;

      default:
        break;
    }
  };

  return (
    <>
      <Switch>
        <Route path="/dealer/clearances/:inventoryItemId" component={InventoryItemCategory} />
        <Route path="/dealer/cart/checkout" component={Checkout} />
        <Route path="/dealer/cart" component={Cart} />
        <Route path="/dealer/useful-links" component={UsefulLinks} />
        <Route path="/dealer/support-chats" component={Supports} />
        <Route path="/dealer/profile" component={Profile} />
        <Route
          path="/dealer/home-page/exterior-signage/check-out/:exteriorSignageQuoteId"
          component={ExteriorSignageQuoteCheckout}
        />
        <Route path="/dealer/home-page/exterior-signage/check-out" component={ExteriorSignageQuoteCheckout} />
        <Route path="/dealer/home-page/exterior-signage/quote" component={ExteriorSignageQuote} />
        <Route path="/dealer/home-page/exterior-signage" component={ExteriorSignageQuoteLandingInfo} />
        <Route path="/dealer/home-page/merchandising-program/check-out" component={MerchandisingProgramQuoteCheckout} />
        <Route path="/dealer/home-page/merchandising-program/quote" component={MerchandisingProgramQuote} />
        <Route path="/dealer/home-page/merchandising-program" component={MerchandisingProgramQuoteLandingInfo} />
        {/* REMOVE WHEN READY TO SHOW NEW PAGE -- IT WILL SIMPLY WORK IF ITS REMOVED */}
        {IsProduction && (
          <Route
            path="/dealer/section/ExteriorSignage/menu/:dealerPortalMenuId"
            component={ExteriorSignageQuoteLandingInfo}
          />
        )}
        {/* REMOVE WHEN READY TO SHOW NEW PAGE -- IT WILL SIMPLY WORK IF ITS REMOVED */}
        {IsProduction && (
          <Route
            path="/dealer/section/MerchandisingServices/menu/:dealerPortalMenuId"
            component={MerchandisingProgramQuoteLandingInfo}
          />
        )}

        <Route
          path="/dealer/section/:sectionName/menu/:dealerPortalMenuId/products/:dealerPortalMenuProductId"
          render={routeProps => {
            const { sectionName } = routeProps?.match?.params || {};

            if (isPrintOnDemandSection(sectionName) && shouldUseLegacyPodUrl()) {
              return <LegacyPodRedirect />;
            }

            if (IsMerchandisingProgram && isDisplaysToolingAndFurnitureSection(sectionName)) {
              return <Redirect to={homePage} />;
            }

            return <DealerPortalMenuProductDetail {...routeProps} />;
          }}
        />

        <Route
          path="/dealer/section/:sectionName/menu/:dealerPortalMenuId"
          render={routeProps => {
            const { sectionName } = routeProps?.match?.params || {};

            if (isPrintOnDemandSection(sectionName) && shouldUseLegacyPodUrl()) {
              return <LegacyPodRedirect />;
            }

            if (IsMerchandisingProgram && isDisplaysToolingAndFurnitureSection(sectionName)) {
              return <Redirect to={homePage} />;
            }

            return <DealerPortalMenuProducts {...routeProps} />;
          }}
        />
        <Route path="/dealer/home-page" component={HomePage} />
        <Route
          path="/dealer/certification"
          render={routeProps => (IsDSM ? <Redirect to={homePage} /> : <Certification {...routeProps} />)}
        />
        {InventoryItemDealerPortalTypeEnum.filter(cat => cat.shownInNavBar && !cat.parentCategory).map(cat => {
          const childrenRoute = InventoryItemDealerPortalTypeEnum.filter(
            c => c.shownInNavBar && c.parentCategory === cat.value,
          );
          if (childrenRoute.length > 0) {
            return childrenRoute.map(scat => {
              const subchildRoute = InventoryItemDealerPortalTypeEnum.filter(
                c => c.shownInNavBar && c.parentCategory === scat.value,
              );

              if (subchildRoute.length > 0) {
                return (
                  <Route
                    key={scat.value}
                    path={`/dealer/${scat.route}/:category/:inventoryItemId`}
                    component={getInventoryTypeEditComponent(scat.name)}
                    exact
                  />
                );
              }

              return (
                <Route
                  key={scat.value}
                  path={`/dealer/${cat.route}/:category/:inventoryItemId`}
                  component={getInventoryTypeEditComponent(scat.name)}
                  exact
                />
              );
            });
          } else {
            return (
              <Route
                key={cat.value}
                path={`/dealer/${cat.route}/:inventoryItemId`}
                component={getInventoryTypeEditComponent(cat.name)}
                exact
              />
            );
          }
        })}
        <Route path="/dealer" component={HomePage} />

        <Redirect to={homePage} />
      </Switch>
    </>
  );
};

export default ApplicationLayout;
